import { useTranslation } from 'react-i18next'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import SendCommunicationEmailForm, { SendCommunicationEmailFormType } from './send-communication-email.hook'
import { Grid, TextField, Typography } from '@vapor/material'
import { Controller } from 'react-hook-form'
import { FormItem } from 'hic-components'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import { faFilePdf, faPaperPlane } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SendCommunicationEmailCrud from './send-communication-email.crud'
import UnsavedChangesTypography from '../unsaved-changes/unsaved-changes-typography/unsaved-changes-typography'
import UnsavedChangesDialog from '../unsaved-changes/unsaved-changes-dialog/unsaved-changes-dialog'
import { useState } from 'react'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'

export default function SendCommunicationEmail({
  open,
  onClose,
  onSendSuccess,
  guestEmail,
  propertyId,
  tenantId,
  reservationId,
  documentId,
  subject,
}: {
  open: boolean
  onClose: () => void
  onSendSuccess: () => void
  propertyId: string
  tenantId: string
  reservationId?: string
  guestEmail?: string
  documentId?: string
  subject?: string
}) {
  const { t } = useTranslation()
  const showError = useErrorSnackBar()
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false)
  const { propertyEmail, messageTemplateId, loading, sendCommunicationEmail, sendAttachmentEmailForDocument } = SendCommunicationEmailCrud({ propertyId, tenantId })
  const { control, errors, isDirty, onFormSubmit } = SendCommunicationEmailForm({
    guestEmail,
    subject,
    onSubmit: async (value) => {
      if (!messageTemplateId) {
        return showError(t('pms.send_communication_email.no_communication_template', { template_code: t('pms.message_template_code.COMMUNICATION') }))
      }

      if (reservationId) {
        const result = await sendCommunicationEmail({
          variables: {
            input: {
              propertyId,
              tenantId,
              reservationId,
              messageTemplateId,
              sendTo: value.sendTo,
              subject: value.subject,
              body: value.body,
            },
          },
        })
        if (!result.errors) {
          onSendSuccess()
        }
      }
      if (documentId) {
        const result = await sendAttachmentEmailForDocument({
          variables: {
            input: {
              documentId,
              messageTemplateId,
              tenantId,
              sendTo: value.sendTo,
              subject: value.subject,
              body: value.body,
            },
          },
        })
        if (!result.errors) {
          onSendSuccess()
        }
      }
    },
  })

  function clickOnClose() {
    if (isDirty) {
      setShowUnsavedChangesDialog(true)
    } else {
      onClose()
    }
  }

  return (
    <FormLayout
      appearance={{
        type: 'DRAWER',
        open,
        onClose: clickOnClose,
        tight: true,
      }}
      title={t('pms.send_communication_email.title')}
      toolbar={
        <FormLayoutDefaultToolbar
          cancelButton={{
            clickHandler: clickOnClose,
            text: t('commons.close'),
            disabled: loading,

            clickConfirmDialog: (
              <UnsavedChangesDialog
                open={showUnsavedChangesDialog}
                handleCloseDialog={() => {
                  setShowUnsavedChangesDialog(false)
                }}
                onCloseAndDiscard={onClose}
              />
            ),
          }}
          leftItem={<UnsavedChangesTypography show={isDirty} />}
          saveButton={{
            clickHandler: onFormSubmit,
            text: t('pms.send_communication_email.send'),
            startIcon: <FontAwesomeIcon icon={faPaperPlane} />,
            disabled: loading,
            loading,
          }}
        />
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body">{t('pms.send_communication_email.from', { from: propertyEmail })}</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller<SendCommunicationEmailFormType, 'sendTo'>
            name="sendTo"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem fullWidth fieldLabel={t('pms.send_communication_email.to')} errorMessage={errors.sendTo?.message}>
                <TextField size="small" value={value ?? ''} onChange={onChange} error={Boolean(errors.sendTo)} />
              </FormItem>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller<SendCommunicationEmailFormType, 'subject'>
            name="subject"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem fullWidth fieldLabel={t('pms.send_communication_email.subject')} errorMessage={errors.subject?.message}>
                <TextField size="small" value={value ?? ''} onChange={onChange} error={Boolean(errors.subject)} />
              </FormItem>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body">{t('pms.send_communication_email.body_note')}</Typography>
        </Grid>
        {documentId && (
          <Grid item xs={12}>
            <FontAwesomeIcon icon={faFilePdf} />
            <Typography variant="body" sx={{ marginLeft: 1 }}>
              {t('pms.send_communication_email.document')}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Controller<SendCommunicationEmailFormType, 'body'>
            name="body"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem fullWidth fieldLabel={t('pms.send_communication_email.body')} errorMessage={errors.body?.message}>
                <TextField size="small" value={value ?? ''} onChange={onChange} error={Boolean(errors.body)} multiline minRows={5} />
              </FormItem>
            )}
          />
        </Grid>
      </Grid>
    </FormLayout>
  )
}
